import suspiciousContactsIcon from '@/assets/smart-alerts/categories/suspicious-contacts.png';
import bullyingHarassmentIcon from '@/assets/smart-alerts/categories/bullying-harassment.png';
import concerningLanguageIcon from '@/assets/smart-alerts/categories/concerning-language.png';
import emotionalDistressIcon from '@/assets/smart-alerts/categories/emotional-distress.png';
import moneyGiftsIcon from '@/assets/smart-alerts/categories/money-gifts.png';
import personalInformationIcon from '@/assets/smart-alerts/categories/personal-information.png';
import drugsAlcoholIcon from '@/assets/smart-alerts/categories/drugs-alcohol.png';

export type SmartAlertPage = {
  slug: string;
  path: string;
  title: string;
  heroSubtitle: string;
  summary: string;
  cardDescription?: string;
  icon: {
    src: string;
    alt: string;
  };
  accentColor: string;
  accentText: string;
  seoTitle?: string;
  seoDescription: string;
  seoKeywords?: string[];
  overview: string[];
  coachingTips: string[];
};

const basePath = '/features/smart-alerts';

export const smartAlertPages: SmartAlertPage[] = [
  {
    slug: 'suspicious-contacts',
    path: `${basePath}/suspicious-contacts`,
    title: 'Suspicious contacts',
    heroSubtitle: 'Quickly spot strangers, burner numbers, or sudden contact churn so you can intervene before conversations go sideways.',
    summary:
      'Joey highlights unfamiliar numbers, sudden spikes in outreach, or contacts that reappear after being blocked. The goal is to give you an early heads-up when someone new is trying to get close to your child.',
    cardDescription: "Flags unknown numbers, sudden contact churn, and outreach patterns that don't feel safe.",
    icon: { src: suspiciousContactsIcon, alt: 'Suspicious contacts icon' },
    accentColor: 'bg-red-100',
    accentText: 'text-red-600',
    seoTitle: 'Suspicious contacts Smart Alert | Joey',
    seoDescription:
      'Learn how Joey Smart Alerts surfaces suspicious iMessage contacts, sudden outreach spikes, and patterns that suggest grooming so you can step in early.',
    overview: [
      'Flags whenever an unknown number drives the conversation or when a contact churns through multiple phone numbers.',
      'Surfaces unusual outreach volume (late-night pings, dozens of unanswered messages, or scripted language).',
      'Pairs every alert with timestamp and context so you can decide whether to block, coach, or escalate.'
    ],
    coachingTips: [
      'Ask open-ended questions: “Do you recognize this person? Do they know your friends?”',
      'Review the alert together and remind your child how to verify real-life connections.',
      'If the contact feels predatory, run a fresh Joey Desktop sync, document screenshots, and block at the carrier level.'
    ]
  },
  {
    slug: 'bullying-harassment',
    path: `${basePath}/bullying-harassment`,
    title: 'Bullying & harassment',
    heroSubtitle: 'Surface intimidation, hostile group chats, or isolating language before it escalates.',
    summary:
      'Repeated put-downs, threats, or coordinated exclusion quickly erode confidence. Joey watches for patterns—not isolated jokes—so you know when words are becoming weapons.',
    cardDescription: 'Surfaces repeated put-downs, intimidation, or language meant to isolate your child.',
    icon: { src: bullyingHarassmentIcon, alt: 'Bullying and harassment icon' },
    accentColor: 'bg-yellow-100',
    accentText: 'text-yellow-600',
    seoTitle: 'Bullying & harassment Smart Alert | Joey',
    seoDescription:
      'See how Joey spots bullying and harassment in iMessage, surfacing repeated put-downs or intimidation so families can respond with context and care.',
    overview: [
      'Clusters similar phrases over time to distinguish ongoing bullying from a single disagreement.',
      'Detects “dog piling” in group chats where multiple kids target the same person.',
      'Highlights escalating severity (from verbal jabs to explicit threats) so you can loop in school leadership if needed.'
    ],
    coachingTips: [
      'Document the alert before conversations get deleted; you may need evidence for school administrators.',
      'Role-play responses with your child and remind them it’s okay to leave a hostile chat.',
      'If the bully is known, set up a mediated conversation with another trusted adult present.'
    ]
  },
  {
    slug: 'concerning-language-hate-speech',
    path: `${basePath}/concerning-language-hate-speech`,
    title: 'Concerning language & hate speech',
    heroSubtitle: 'Pick up on discriminatory language, slurs, or rhetoric that suggests conversations are headed in a dangerous direction.',
    summary:
      'Joey keeps an ear out for hateful or radicalizing language, not to censor kids but to help families address biases, self-harm talk, or outright calls to violence.',
    cardDescription: 'Detects escalating language, slurs, or harmful rhetoric that signals conversations are off track.',
    icon: { src: concerningLanguageIcon, alt: 'Concerning language icon' },
    accentColor: 'bg-purple-100',
    accentText: 'text-purple-600',
    seoTitle: 'Concerning language & hate speech Smart Alert | Joey',
    seoDescription:
      'Understand how Joey Smart Alerts detects hate speech, slurs, or escalating rhetoric so you can course-correct conversations before they spiral.',
    overview: [
      'Detects a wide range of slurs and coded language that often slips past standard profanity filters.',
      'Flags when conversations glorify violence or promote extremist talking points.',
      'Surfaces self-directed harmful language so you can check in on emotional wellbeing.'
    ],
    coachingTips: [
      'Use alerts as a starting point for values-based conversations around respect and inclusion.',
      'Remind teens that “just repeating a meme” still lands as hate speech for someone else.',
      'Escalate to professional help if hateful or violent language is paired with threats toward self or others.'
    ]
  },
  {
    slug: 'emotional-distress',
    path: `${basePath}/emotional-distress`,
    title: 'Emotional distress',
    heroSubtitle: 'Look for early signals of anxiety, withdrawal, or self-harm language so you can check in before things get worse.',
    summary:
      'Joey monitors tone and phrasing—not just keywords—to surface when a child sounds overwhelmed, checked out, or like they’re masking deeper pain.',
    cardDescription: 'Looks for signals of anxiety, withdrawal, or self-harm language so parents can check in early.',
    icon: { src: emotionalDistressIcon, alt: 'Emotional distress icon' },
    accentColor: 'bg-cyan-100',
    accentText: 'text-cyan-600',
    seoTitle: 'Emotional distress Smart Alert | Joey',
    seoDescription:
      'Joey Smart Alerts highlight language that points to burnout, depression, or self-harm ideation so you can offer help quickly and compassionately.',
    overview: [
      'Detects “I can’t do this anymore,” “no one would miss me,” and other self-harm indicators.',
      'Looks for withdrawal markers like “I’m done with everyone” or “just leave me alone forever.”',
      'Pairs alerts with context so you can see whether it was a fleeting vent or ongoing struggle.'
    ],
    coachingTips: [
      'Respond with empathy before offering solutions; teens need to feel heard first.',
      'If language indicates imminent risk, contact local crisis resources and keep a close eye on upcoming syncs.',
      'Encourage journaling or therapy to process big feelings outside of chat apps.'
    ]
  },
  {
    slug: 'money-and-gifts',
    path: `${basePath}/money-and-gifts`,
    title: 'Money & gifts',
    heroSubtitle: 'Spot cash requests, gift-card pressure, or financial grooming tactics before your child is out any money.',
    summary:
      'Joey surfaces when someone nudges your child to send cash, gift cards, skins, or passwords tied to financial accounts.',
    cardDescription: 'Highlights payment requests, gift-card pressure, and money drops mixed with urgency cues.',
    icon: { src: moneyGiftsIcon, alt: 'Money and gifts icon' },
    accentColor: 'bg-amber-100',
    accentText: 'text-amber-600',
    seoTitle: 'Money & gifts Smart Alert | Joey',
    seoDescription:
      'Learn how Joey flags money and gift requests in iMessage so you can shut down scams, fraud, or manipulative gift giving.',
    overview: [
      'Highlights urgent payment language (“do it now,” “I need the code before midnight”).',
      'Differentiates normal gifting between friends from leveraged or guilt-based requests.',
      'Offers context so you can tell if the sender is a known friend, an older teen, or a stranger.'
    ],
    coachingTips: [
      'Review family rules around purchases and why gift-card requests are a scam red flag.',
      'If your child already sent money, capture receipts and screenshots before contacting the platform.',
      'Discuss healthy boundaries for lending money, even to well-meaning friends.'
    ]
  },
  {
    slug: 'personal-information',
    path: `${basePath}/personal-information`,
    title: 'Personal information (PII)',
    heroSubtitle: 'Warns when kids are asked to share addresses, passwords, schedules, or other details that should stay private.',
    summary:
      'Whether it’s a new crush asking for a home address or a scammer fishing for Apple ID codes, Joey surfaces PII conversations so you can reinforce safe sharing habits.',
    cardDescription: 'Warns when kids are asked to share addresses, passwords, location, or contact details.',
    icon: { src: personalInformationIcon, alt: 'Personal information icon' },
    accentColor: 'bg-orange-100',
    accentText: 'text-orange-600',
    seoTitle: 'Personal information Smart Alert | Joey',
    seoDescription:
      'Joey Smart Alerts catch when iMessage conversations include requests for addresses, passwords, or account resets so families can intervene fast.',
    overview: [
      'Detects when someone asks for security codes, Apple ID, or school schedules.',
      'Flags oversharing from your child too, not just requests from others.',
      'Provides quick coaching prompts so you can teach polite but firm refusal scripts.'
    ],
    coachingTips: [
      'Remind kids that legit companies or friends will never need verification codes sent over chat.',
      'Practice responses like “I don’t share that online” so they’re ready in the moment.',
      'If sensitive info already leaked, rotate passwords and consider a temporary device lock-down.'
    ]
  },
  {
    slug: 'drugs-and-alcohol',
    path: `${basePath}/drugs-and-alcohol`,
    title: 'Drugs & alcohol',
    heroSubtitle: 'Catches references to substance use, supply offers, or planning that needs adult guidance.',
    summary:
      'Joey listens for invitations to parties with risky behavior, “plug” mentions, or jokes about mixing pills and alcohol.',
    cardDescription: 'Catches references to substance use, supply offers, or planning that needs adult guidance.',
    icon: { src: drugsAlcoholIcon, alt: 'Drugs and alcohol icon' },
    accentColor: 'bg-pink-100',
    accentText: 'text-pink-600',
    seoTitle: 'Drugs & alcohol Smart Alert | Joey',
    seoDescription:
      'Understand how Joey flags drug and alcohol conversations so you can set expectations, support your child, and intervene before experimentation escalates.',
    overview: [
      'Highlights supply language (“plug,” “drop,” “got the stuff”) and pressure to join parties.',
      'Differentiates between kids venting about others vs. explicit self-use planning.',
      'Captures timelines so you know whether something is hypothetical or happening this weekend.'
    ],
    coachingTips: [
      'Stay calm—if kids fear punishment, they’ll hide future alerts.',
      'Discuss alternative plans or safe exit strategies for parties that suddenly feel uncomfortable.',
      'Loop in healthcare professionals if alerts suggest habitual use or mixing meds unsafely.'
    ]
  }
];

export const smartAlertPageMap = Object.fromEntries(
  smartAlertPages.map((page) => [page.slug, page])
);
